import React from 'react';
import { humanizedTime } from 'es6/helpers/time';

/**
 * NOTE: Anything that mixes this in needs to define a getStatString() method.
 */
const StatsMixin = {

  /**
   * Can be used to override localized strings.
   *
   * @param {String} [started|items-zone-1|items-zone-2|time-studied] type
   * @returns {String}
   *
   * Example method:
   *
   *     getStatString: function(type) {
   *       switch(type) {
   *         case 'started':
   *           return 'started string';
   *         case 'items-zone-1':
   *           return 'items zone 1 string';
   *         case 'items-zone-2':
   *           return 'items zone 2 string';
   *         case 'time-studied':
   *           return 'time studied string';
   *       }
   *
   *       return '';
   *     }
   */

  /**
   * Renders a stat tablet.
   *
   * @param {String} type
   * @param {ReactElement|String} primary
   * @param {ReactElement|String} secondary
   * @returns {ReactElement}
   */
  renderStat(type, primary, secondary) {
    return (
      <li key={type} className={`content-column ${type}`}>
        <aside className="accent" />
        <section className="item-data">
          <span className="primary">{primary}</span>
          <span className="secondary">{secondary}</span>
        </section>
      </li>
    );
  },

  /**
   * Renders the started, strong and mastered stats as a list of items.
   * It does not include the <ul> element, as some panels also have
   * an extra element.
   *
   * @param {Goal} goal
   * @param {AggregateMemory} aggregate
   * @param {Boolean} includeTimeStudied
   * @returns {ReactElement[]}
   */
  renderStats(goal, aggregate, includeTimeStudied = true) {
    let timeStudied = null;

    if (includeTimeStudied) {
      timeStudied = this.renderStat(
        'time',
        (<span className="time-studied" dangerouslySetInnerHTML={{ __html: humanizedTime(aggregate.items.totalStudyTime) }} />),
        this.getStatString('time-studied')
      );
    }

    return [
      this.renderStat(
        'started',
        [
          (<span key="primary-main" className="primary-main">{aggregate.items.studiedItemsCount}</span>),
          (<span key="primary-sub" className="primary-sub">/{goal.getTotalItemsCount(aggregate)}</span>),
        ],
        this.getStatString('started')
      ),
      this.renderStat('strong', aggregate.items.getItemsAtCheckpoint1(), this.getStatString('items-zone-1')),
      this.renderStat('mastered', aggregate.items.itemsAtCheckpoint2, this.getStatString('items-zone-2')),
      timeStudied,
    ];
  },
};

export default StatsMixin;
